import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { useApiStatus } from "@common/hooks/useApiStatus"

let lastSender = 0

/**
 * Sends a request on an api slot that other mounted screens use too, and
 * hears back the answer to its own request only.
 *
 * `useApiStatus` releases the slot for every listener that sees a success, so
 * a listener that stays mounted consumes the answers other screens wait for.
 * Here the request is signed in its route params, which the slot hands back
 * with the answer: an answer signed by someone else is left alone, and one of
 * ours is still ours when apiRequest re-sends it (offline « Réessayer »,
 * expired session).
 *
 * `useApiMutation` (ports) guards the new screens with a flag instead; a flag
 * does not survive those re-sends.
 *
 * Only for a service that destructures its route params: one that forwards
 * them whole would send the signature to the server.
 *
 * @returns {(routeParams?: object, formData?: object, urlParams?: object) => void} send
 */
const useOwnApiAnswer = (service, onSuccess = () => {}, onError = () => {}) => {
    const dispatch = useDispatch()
    const senderRef = useRef(null)
    senderRef.current ??= ++lastSender

    const send = (routeParams, formData, urlParams) => {
        dispatch(
            apiRequest(
                service,
                { ...routeParams, answerTo: senderRef.current },
                formData,
                urlParams
            )
        )
    }

    const isOurs = (routeParams) => routeParams?.answerTo === senderRef.current

    useApiStatus(
        service,
        null,
        false,
        (data, allData, urlParams, routeParams) => {
            if (!isOurs(routeParams)) return
            dispatch(apiIdle(service))
            onSuccess(data, allData, urlParams, routeParams)
        },
        (error, urlParams, routeParams) => {
            if (!isOurs(routeParams)) return
            dispatch(apiIdle(service))
            onError(error, urlParams, routeParams)
        },
        true
    )

    // useApiStatus only calls back on an error that carries a message; a
    // request that never got an answer (network drop, timeout) carries none,
    // and its sender would never hear it failed.
    const slot = useSelector((state) => state.api?.[service?.name])
    useEffect(() => {
        if (!slot?.error || slot.error.message || !isOurs(slot.routeParams)) return
        dispatch(apiIdle(service))
        onError(slot.error, slot.urlParams, slot.routeParams)
    }, [slot?.error])

    return send
}

export default useOwnApiAnswer
